import { useState, useEffect, useCallback, Fragment } from "react";
import {
  Loader2, Save, Pencil, FilePlus2, ChevronDown, Eye, History, GitCompareArrows, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { FieldRow, NullableNumber, MoneyField, SwitchRow, StatusBadge } from "./commercial/CommercialFields";
import {
  PLAN_KEYS, PLAN_LABELS, BILLING_OPTIONS, BILLING_LABELS, EXP_OPTIONS, EXP_LABELS,
  CAP_FIELDS, INC_FIELDS, DRAFT_FIELDS, numOrDash, fmtDate,
} from "../lib/commercial";

const PRICE_FIELDS = ["base_price_minor", "additional_member_price_minor", "requires_quote"];

const ComparisonTable = ({ runtime, draft }) => {
  if (!runtime || !draft) return null;
  const rows = [
    ["Nom", runtime.name, draft.name],
    ["Modèle de facturation", BILLING_LABELS[runtime.billing_model] || runtime.billing_model, BILLING_LABELS[draft.billing_model] || draft.billing_model],
    ["Utilisateurs inclus", numOrDash(runtime.included_members), numOrDash(draft.included_members)],
    ["Seuil mensuel de secrets", numOrDash(runtime.monthly_secret_soft_limit), numOrDash(draft.monthly_secret_soft_limit)],
    ["Plafond mensuel de secrets", numOrDash(runtime.monthly_secret_hard_limit), numOrDash(draft.monthly_secret_hard_limit)],
    ["Demandes sécurisées / mois", numOrDash(runtime.monthly_secure_request_limit), numOrDash(draft.monthly_secure_request_limit)],
    ["Conservation historique (j)", numOrDash(runtime.history_retention_days), numOrDash(draft.history_retention_days)],
    ["Expiration maximale", EXP_LABELS[runtime.max_expiration] || runtime.max_expiration, EXP_LABELS[draft.max_expiration] || draft.max_expiration],
    ["Domaines personnalisés max", numOrDash(runtime.max_custom_domains), numOrDash(draft.max_custom_domains)],
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="comparison-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2 font-medium">Champ</th>
            <th className="px-4 py-2 font-medium">Runtime actuel</th>
            <th className="px-4 py-2 font-medium">Brouillon</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, rt, dr]) => {
            const diff = String(rt) !== String(dr);
            return (
              <tr key={label} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-500">{label}</td>
                <td className="px-4 py-2 text-slate-700">{String(rt)}</td>
                <td className={`px-4 py-2 font-medium ${diff ? "text-amber-700 bg-amber-50" : "text-slate-700"}`}>
                  {String(dr)}{diff && <span className="ml-1.5 text-[10px] uppercase text-amber-600">modifié</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------- Éditeur d'un plan (déplié sous sa ligne) ----------------
const PlanEditor = ({ planKey, onSaved }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [formVersion, setFormVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmVersion, setConfirmVersion] = useState(false);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/commercial/plans/${planKey}`);
      setData(data);
      setDraft(data.draft);
      setFormVersion((v) => v + 1);
    } catch (e) {
      toast.error("Impossible de charger le plan.");
    }
  }, [planKey]);

  useEffect(() => { load(); }, [load]);

  const upd = (f, v) => setDraft((d) => ({ ...d, [f]: v }));

  const buildPayload = () => {
    const out = {};
    DRAFT_FIELDS.forEach((f) => {
      if (f === "pricing") {
        const pricing = {};
        Object.keys(draft.pricing || {}).forEach((rc) => {
          const src = draft.pricing[rc] || {};
          const rp = {};
          PRICE_FIELDS.forEach((pf) => (rp[pf] = src[pf] ?? (pf.endsWith("_minor") ? null : false)));
          pricing[rc] = rp;
        });
        out.pricing = pricing;
      } else {
        out[f] = draft[f] ?? null;
      }
    });
    ["active", "visible", "can_use_branding", "can_use_custom_domain", "can_use_api",
      "can_use_webhooks", "can_use_secure_requests", "can_use_recipient_otp",
      "can_use_sms_delivery", "can_use_sso", "includes_webhooks", "includes_recipient_otp",
      "includes_sms_delivery", "includes_sso", "requires_quote"].forEach((b) => (out[b] = !!out[b]));
    out.display_order = out.display_order ?? 0;
    return out;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/commercial/plans/${planKey}/draft`, buildPayload());
      toast.success("Brouillon enregistré (préparation).");
      await load();
      onSaved && onSaved();
    } catch (e) {
      const d = apiError(e.response?.data?.detail);
      toast.error(/hard|soft/i.test(d) ? "Le plafond doit être ≥ au seuil indicatif." : (d || "Échec de l'enregistrement."));
    } finally { setSaving(false); }
  };

  const createVersion = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/admin/commercial/plans/${planKey}/versions`);
      toast.success(`Version ${data.version} créée.`);
      setConfirmVersion(false);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de la création de version.");
    } finally { setCreating(false); }
  };

  if (!draft) {
    return <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div data-testid={`plan-editor-${planKey}`} className="pt-2">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Button data-testid={`save-draft-btn-${planKey}`} onClick={save} disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer le brouillon
        </Button>
        <Button data-testid={`create-version-btn-${planKey}`} onClick={() => setConfirmVersion(true)} variant="outline" className="border-slate-200">
          <FilePlus2 className="w-4 h-4 mr-2" /> Créer une version
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["general"]} className="space-y-2">
        <AccordionItem value="general" data-testid="section-general" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">Général</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-2">
              <FieldRow label="Nom du plan">
                <Input data-testid="field-name" value={draft.name || ""} onChange={(e) => upd("name", e.target.value)} className="bg-slate-50 border-slate-200" />
              </FieldRow>
              <FieldRow label="Ordre d'affichage">
                <NullableNumber testid="field-display-order" value={draft.display_order} onChange={(v) => upd("display_order", v ?? 0)} placeholder="0" />
              </FieldRow>
              <FieldRow label="Modèle de facturation">
                <Select value={draft.billing_model} onValueChange={(v) => upd("billing_model", v)}>
                  <SelectTrigger data-testid="field-billing-model" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLING_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              <div className="space-y-2.5">
                <SwitchRow testid="field-active" label="Plan actif" checked={draft.active} onChange={(v) => upd("active", v)} />
                <SwitchRow testid="field-visible" label="Plan visible" checked={draft.visible} onChange={(v) => upd("visible", v)} />
                <SwitchRow testid="field-requires-quote" label="Sur devis" checked={draft.requires_quote} onChange={(v) => upd("requires_quote", v)} />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="users" data-testid="section-users" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">Utilisateurs</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-2">
              <FieldRow label="Utilisateurs inclus">
                <NullableNumber testid="field-included-members" value={draft.included_members} onChange={(v) => upd("included_members", v)} />
              </FieldRow>
              <FieldRow label="Maximum en souscription libre">
                <NullableNumber testid="field-self-service-max" value={draft.self_service_max_members} onChange={(v) => upd("self_service_max_members", v)} />
              </FieldRow>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="volumes" data-testid="section-volumes" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">Volumes</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-2">
              <FieldRow label="Seuil mensuel indicatif de secrets">
                <NullableNumber testid="field-soft-limit" value={draft.monthly_secret_soft_limit} onChange={(v) => upd("monthly_secret_soft_limit", v)} />
              </FieldRow>
              <FieldRow label="Plafond mensuel de secrets" help="Doit être ≥ au seuil indicatif.">
                <NullableNumber testid="field-hard-limit" value={draft.monthly_secret_hard_limit} onChange={(v) => upd("monthly_secret_hard_limit", v)} />
              </FieldRow>
              <FieldRow label="Demandes sécurisées par mois">
                <NullableNumber testid="field-secure-request-limit" value={draft.monthly_secure_request_limit} onChange={(v) => upd("monthly_secure_request_limit", v)} />
              </FieldRow>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="retention" data-testid="section-retention" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">Conservation et limites</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-2">
              <FieldRow label="Conservation de l'historique (jours)">
                <NullableNumber testid="field-history-retention" value={draft.history_retention_days} onChange={(v) => upd("history_retention_days", v)} />
              </FieldRow>
              <FieldRow label="Expiration maximale d'un secret">
                <Select value={draft.max_expiration} onValueChange={(v) => upd("max_expiration", v)}>
                  <SelectTrigger data-testid="field-max-expiration" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXP_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Domaines personnalisés maximum">
                <NullableNumber testid="field-max-custom-domains" value={draft.max_custom_domains} onChange={(v) => upd("max_custom_domains", v)} />
              </FieldRow>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="capabilities" data-testid="section-capabilities" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">Capacités techniques</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 pb-2">
              {CAP_FIELDS.map(([f, l]) => <SwitchRow key={f} testid={`cap-${f}`} label={l} checked={draft[f]} onChange={(v) => upd(f, v)} />)}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="includes" data-testid="section-includes" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">Inclusions commerciales</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 pb-2">
              {INC_FIELDS.map(([f, l]) => <SwitchRow key={f} testid={`inc-${f}`} label={`Inclus : ${l}`} checked={draft[f]} onChange={(v) => upd(f, v)} />)}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Comparaison runtime — repliable */}
        <AccordionItem value="comparison" data-testid="section-comparison" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">
            <span className="flex items-center gap-2"><GitCompareArrows className="w-4 h-4 text-emerald-600" /> Comparaison avec le runtime actuel</span>
          </AccordionTrigger>
          <AccordionContent><div className="pb-2"><ComparisonTable runtime={data?.runtime_current} draft={draft} /></div></AccordionContent>
        </AccordionItem>

        {/* Historique des versions — repliable */}
        <AccordionItem value="history" data-testid="section-history" className="border border-slate-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-semibold">
            <span className="flex items-center gap-2"><History className="w-4 h-4 text-emerald-600" /> Historique des versions</span>
          </AccordionTrigger>
          <AccordionContent>
            {(!data?.versions || data.versions.length === 0) ? (
              <p className="text-sm text-slate-400 py-2" data-testid="versions-empty">Aucune version préparée pour le moment.</p>
            ) : (
              <div className="space-y-2 pb-2">
                {data.versions.map((v) => (
                  <div key={v.id} data-testid={`version-row-${v.version}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm">Version {v.version}</span>
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-400/40">Préparée</Badge>
                    </div>
                    <div className="text-xs text-slate-400">Créée le {fmtDate(v.created_at)}</div>
                    <Button data-testid={`version-view-${v.version}`} size="sm" variant="outline" className="border-slate-200" onClick={() => setViewing(v)}>
                      <Eye className="w-4 h-4 mr-1.5" /> Consulter
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Dialog open={confirmVersion} onOpenChange={setConfirmVersion}>
        <DialogContent data-testid="confirm-version-dialog">
          <DialogHeader>
            <DialogTitle>Créer une version</DialogTitle>
            <DialogDescription>Crée un snapshot immuable du brouillon actuel (préparé, non appliqué).</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setConfirmVersion(false)}>Annuler</Button>
            <Button data-testid="confirm-version-ok" onClick={createVersion} disabled={creating} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FilePlus2 className="w-4 h-4 mr-2" />} Créer la version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="version-view-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Version {viewing?.version} — lecture seule</DialogTitle>
            <DialogDescription>Snapshot immuable, préparé le {fmtDate(viewing?.created_at)}.</DialogDescription>
          </DialogHeader>
          {viewing?.snapshot && (
            <div className="text-sm space-y-1">
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Nom</span><span className="font-medium">{viewing.snapshot.name}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Facturation</span><span className="font-medium">{BILLING_LABELS[viewing.snapshot.billing_model] || viewing.snapshot.billing_model}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Utilisateurs inclus</span><span className="font-medium">{numOrDash(viewing.snapshot.included_members)}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Expiration max</span><span className="font-medium">{EXP_LABELS[viewing.snapshot.max_expiration] || viewing.snapshot.max_expiration}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------- Éditeur Anonyme (volumes/limites administrables) ----------------
const AnonEditor = () => {
  const [s, setS] = useState(null);
  const [choices, setChoices] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/commercial/anon-settings");
    setS(data.settings);
    setChoices(data.expiration_choices || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleExp = (code) => setS((p) => {
    const set = new Set(p.allowed_expirations);
    if (set.has(code)) set.delete(code); else set.add(code);
    let def = p.default_expiration;
    if (!set.has(def)) def = Array.from(set)[0] || def;
    return { ...p, allowed_expirations: Array.from(set), default_expiration: def };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/commercial/anon-settings", {
        quota_hour: s.quota_hour, quota_day: s.quota_day,
        allowed_expirations: s.allowed_expirations, default_expiration: s.default_expiration,
      });
      toast.success("Paramètres de l'usage anonyme enregistrés.");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement.");
    } finally { setSaving(false); }
  };

  if (!s) return <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div data-testid="plan-editor-anonymous" className="pt-2 space-y-4">
      <p className="text-xs text-slate-500">
        L'utilisation sans compte reste techniquement distincte d'un plan commercial. Seuls les volumes et
        limites anti-abus sont administrables ici ; les mécanismes de sécurité internes ne le sont pas.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label="Créations maximum par heure (par IP / session)">
          <NullableNumber testid="anon-quota-hour" value={s.quota_hour} onChange={(v) => setS((p) => ({ ...p, quota_hour: v ?? 1 }))} placeholder="3" />
        </FieldRow>
        <FieldRow label="Créations maximum par 24 h (par IP / session)">
          <NullableNumber testid="anon-quota-day" value={s.quota_day} onChange={(v) => setS((p) => ({ ...p, quota_day: v ?? 1 }))} placeholder="10" />
        </FieldRow>
      </div>
      <div>
        <div className="text-sm text-slate-600 mb-2">Durées d'expiration autorisées (≤ 4 h pour l'anonyme)</div>
        <div className="flex flex-wrap gap-3">
          {choices.map((code) => (
            <label key={code} data-testid={`anon-exp-${code}`} className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 cursor-pointer">
              <Checkbox checked={s.allowed_expirations.includes(code)} onCheckedChange={() => toggleExp(code)} />
              <span className="text-sm">{EXP_LABELS[code] || code}</span>
            </label>
          ))}
        </div>
      </div>
      <FieldRow label="Durée par défaut">
        <Select value={s.default_expiration} onValueChange={(v) => setS((p) => ({ ...p, default_expiration: v }))}>
          <SelectTrigger data-testid="anon-default-expiration" className="bg-slate-50 border-slate-200 max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{s.allowed_expirations.map((c) => <SelectItem key={c} value={c}>{EXP_LABELS[c] || c}</SelectItem>)}</SelectContent>
        </Select>
      </FieldRow>
      <Button data-testid="save-anon-btn" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
      </Button>
    </div>
  );
};

// ==================== Tableau des plans ====================
export const CommercialPlans = () => {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(null); // 'anonymous' | plan_key | null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/commercial/catalog");
      setCatalog(data);
      setError(false);
    } catch (e) { setError(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div data-testid="plans-error" className="glass rounded-2xl p-8 text-center text-slate-600">Catalogue indisponible. <Button variant="outline" className="ml-2" onClick={load}>Réessayer</Button></div>;
  if (!catalog) return <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const planMap = Object.fromEntries((catalog.plans || []).map((p) => [p.plan_key, p]));
  const rows = [
    { key: "anonymous", anon: true },
    ...PLAN_KEYS.map((k) => ({ key: k, plan: planMap[k] })),
  ];

  const toggle = (k) => setOpen((cur) => (cur === k ? null : k));

  return (
    <div data-testid="commercial-plans" className="glass rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="plans-table">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-medium">Nom du plan</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Visible</th>
              <th className="px-4 py-3 font-medium">Versions</th>
              <th className="px-4 py-3 font-medium text-right">Modifier</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isOpen = open === row.key;
              const d = row.plan?.draft || {};
              const label = row.anon ? "Anonyme" : (d.name || PLAN_LABELS[row.key]);
              return (
                <Fragment key={row.key}>
                  <tr data-testid={`plan-row-${row.key}`} className={`border-t border-slate-100 ${isOpen ? "bg-emerald-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{label}</div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-400">{row.anon ? "sans compte" : row.key}</div>
                    </td>
                    <td className="px-4 py-3" data-testid={`plan-status-${row.key}`}>
                      {row.anon ? <span className="text-slate-300">—</span> : <StatusBadge ok={d.active} yes="Actif" no="Inactif" />}
                    </td>
                    <td className="px-4 py-3" data-testid={`plan-visible-${row.key}`}>
                      {row.anon ? <span className="text-slate-300">—</span> : <StatusBadge ok={d.visible} yes="Visible" no="Non visible" />}
                    </td>
                    <td className="px-4 py-3" data-testid={`plan-versions-${row.key}`}>
                      {row.anon ? <span className="text-slate-300">—</span> : <span className="text-slate-600">{row.plan?.prepared_versions ?? 0}</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button data-testid={`plan-edit-${row.key}`} size="icon" variant="outline" className="border-slate-200 h-8 w-8" onClick={() => toggle(row.key)}>
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                      </Button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-slate-100 bg-white">
                      <td colSpan={5} className="px-4 py-4">
                        {row.anon ? <AnonEditor /> : <PlanEditor planKey={row.key} onSaved={load} />}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
